import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Spinner,
  Offcanvas,
  OffcanvasHeader,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import SupplierService from "../../../services/OrderingServices/SupplierService";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  contactPerson: Yup.string(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  address: Yup.string().required("Address is required"),
  panNumber: Yup.string().required("PAN Number is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  city: Yup.string(),
});

const SupplierForm = ({ modal, toggleModal, fetchSuppliers, selectedItem }) => {
  const [orgDetail, setOrgDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch supplier detail only if editing
useEffect(() => {
  if (!selectedItem) {
    setOrgDetail(null); // Reset for new supplier
    return;
  }

  const fetchDetail = async () => {
    try {
      // If selectedItem is an object, get the id
      const id = selectedItem.id || selectedItem; 
      const response = await SupplierService.detail(id);
      setOrgDetail(response);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch supplier details");
    }
  };

  fetchDetail();
}, [selectedItem]);

  // Formik initialValues depend on orgDetail
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: orgDetail
      ? {
          id: orgDetail.id || "",
          name: orgDetail.name || "",
          contactPerson: orgDetail.contactPerson || "",
          email: orgDetail.email || "",
          address: orgDetail.address || "",
          phone: orgDetail.phone || "",
          panNumber: orgDetail.panNumber || "",
          city: orgDetail.city || "",
        }
      : {
          id: "",
          name: "",
          contactPerson: "",
          email: "",
          address: "",
          phone: "",
          panNumber: "",
          city: "",
        },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        if (values.id) {
          await SupplierService.update(values.id, values);
          toast.success("Supplier updated successfully");
        } else {
          await SupplierService.create(values);
          toast.success("New supplier added successfully");
        }
        await fetchSuppliers();
        resetForm();
        toggleModal();
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  // If orgDetail is not loaded yet for edit, you can show a spinner
  if (selectedItem && !orgDetail) {
    return (
      <Offcanvas isOpen={modal} toggle={toggleModal} direction="end">
        <OffcanvasHeader toggle={toggleModal}>
          Loading supplier...
        </OffcanvasHeader>
        <div className="p-3 text-center">
          <Spinner />
        </div>
      </Offcanvas>
    );
  }

  return (
    <Offcanvas
      isOpen={modal}
      toggle={toggleModal}
      direction="end"
      backdrop={true}
      style={{ width: "420px" }}
    >
      <OffcanvasHeader toggle={toggleModal}>
        {selectedItem ? "Update Supplier" : "Create Supplier"}
      </OffcanvasHeader>

      <Form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column"
        style={{ height: "calc(100vh - 73px)" }}
      >
        <div className="flex-grow-1 overflow-auto p-3">
          {/** Supplier Name */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Supplier Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name && (
              <FormFeedback>{formik.errors.name}</FormFeedback>
            )}
          </div>

          {/** Contact Person */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Contact Person</Label>
            <Input
              id="contactPerson"
              name="contactPerson"
              type="text"
              placeholder="Contact Person"
              value={formik.values.contactPerson}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.contactPerson && formik.errors.contactPerson)}
            />
            {formik.touched.contactPerson && formik.errors.contactPerson && (
              <FormFeedback>{formik.errors.contactPerson}</FormFeedback>
            )}
          </div>

          {/** Email */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.email && formik.errors.email)}
            />
            {formik.touched.email && formik.errors.email && <FormFeedback>{formik.errors.email}</FormFeedback>}
          </div>

          {/** Phone */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              type="text"
              placeholder="Phone Number"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.phone && formik.errors.phone)}
            />
            {formik.touched.phone && formik.errors.phone && <FormFeedback>{formik.errors.phone}</FormFeedback>}
          </div>

          {/** Address */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Address</Label>
            <Input
              id="address"
              name="address"
              type="text"
              placeholder="Enter address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.address && formik.errors.address)}
            />
            {formik.touched.address && formik.errors.address && <FormFeedback>{formik.errors.address}</FormFeedback>}
          </div>

          {/** City */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">City</Label>
            <Input
              id="city"
              name="city"
              type="text"
              placeholder="City"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.city && formik.errors.city)}
            />
            {formik.touched.city && formik.errors.city && <FormFeedback>{formik.errors.city}</FormFeedback>}
          </div>

          {/** PAN Number */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">PAN Number</Label>
            <Input
              id="panNumber"
              name="panNumber"
              type="text"
              placeholder="PAN Number"
              value={formik.values.panNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.panNumber && formik.errors.panNumber)}
            />
            {formik.touched.panNumber && formik.errors.panNumber && <FormFeedback>{formik.errors.panNumber}</FormFeedback>}
          </div>
        </div>

        {/** Footer buttons */}
        <div className="bg-white border-top p-3 d-flex gap-2">
          <Button color="danger" className="w-50" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="success" className="w-50" type="submit" disabled={loading}>
            {loading && <Spinner size="sm" className="me-2" />}
            {selectedItem ? (loading ? "Updating..." : "Update") : loading ? "Creating..." : "Create"}
          </Button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default SupplierForm;
